/**
 * `ad:filename` is the portable, region-agnostic form static-ad agents save
 * into markup; the editor and any other renderer is expected to rewrite it
 * to a regional `${apiBase}/agents/files?key=…` URL before painting. This
 * module also handles round-tripping the original ad ref via `data-ad-ref`
 * so the editor's serialize step can restore the portable form on save.
 *
 * Runs in both the editor (native browser DOMParser) and the backend
 * Figma-plugin endpoint (jsdom); `./dom-env` installs jsdom's window globals
 * on Node so the standard Element-tree APIs work identically here.
 */

import "./dom-env";

import {
  type AdRefImageHints,
  injectHintsIntoAgentFileUrl,
  parseAgentFileUrl,
} from "../agent-file-url";

export {
  type AdRefImageHints,
  rawAgentFileUrlFallback,
  rawAgentFileUrlForAdRef,
} from "../agent-file-url";

const AD_REF_PREFIX = "ad:";
const AD_REF_DATA_ATTR = "data-ad-ref";
/** Shared with every other renderer that has to walk the same refs. */
export const URL_FUNCTION_RE =
  /url\(\s*(?:"([^"]+)"|'([^']+)'|([^)'"\\\s]+))\s*\)/g;

/**
 * Every attribute that can carry an `ad:` ref. Exported because a renderer that
 * resolves these refs must also handle whatever it resolved them to (signing a
 * URL, say) — keeping a second copy of the list is how `<use>` gets missed.
 */
export const AD_REF_ATTRIBUTES = [
  { selector: "img", attr: "src" },
  { selector: "image", attr: "href" },
  { selector: "image", attr: "xlink:href" },
  { selector: "use", attr: "href" },
  { selector: "use", attr: "xlink:href" },
] as const;

const SRCSET_SELECTOR = "img[srcset], source[srcset]";
// A candidate URL runs to whitespace (data: URLs keep their commas); trailing commas end the candidate, per the HTML srcset parser.
const SRCSET_URL_RE = /(^|,)(\s*)(\S+?)(?=,*(?:\s|$))/g;

/** Rewrites each candidate URL and leaves the rest of the srcset verbatim. */
function mapSrcsetUrls(srcset: string, map: (url: string) => string): string {
  return srcset.replace(
    SRCSET_URL_RE,
    (_, comma: string, space: string, url: string) => comma + space + map(url),
  );
}

/** Collect the same portable asset references that the renderer resolves. */
export function collectAdRefFilenames(markup: string): Set<string> {
  const root = new DOMParser().parseFromString(
    markup,
    "text/html",
  ).documentElement;
  const filenames = new Set<string>();
  const collect = (url: string | null | undefined) => {
    if (url?.startsWith(AD_REF_PREFIX)) {
      filenames.add(url.slice(AD_REF_PREFIX.length));
    }
  };

  for (const { selector, attr } of AD_REF_ATTRIBUTES) {
    for (const el of getMatchingElements(root, selector)) {
      collect(el.getAttribute(attr));
    }
  }
  for (const el of getMatchingElements(root, SRCSET_SELECTOR)) {
    mapSrcsetUrls(el.getAttribute("srcset") ?? "", (url) => {
      collect(url);
      return url;
    });
  }

  const styles = [
    ...getElementsWithStyle(root).map((el) => el.getAttribute("style")),
    ...Array.from(root.querySelectorAll("style")).map((el) => el.textContent),
  ];
  for (const style of styles) {
    if (!style) continue;
    for (const match of style.matchAll(URL_FUNCTION_RE)) {
      collect(match[1] ?? match[2] ?? match[3]);
    }
  }
  return filenames;
}

/** Maps every URL the renderer would resolve as an `ad:` ref, leaving links and text alone. */
export function mapAdRefUrls(
  root: Element,
  map: (url: string) => string,
): void {
  for (const { selector, attr } of AD_REF_ATTRIBUTES) {
    for (const el of getMatchingElements(root, selector)) {
      const url = el.getAttribute(attr);
      if (url && map(url) !== url) el.setAttribute(attr, map(url));
    }
  }
  for (const el of getMatchingElements(root, SRCSET_SELECTOR)) {
    el.setAttribute(
      "srcset",
      mapSrcsetUrls(el.getAttribute("srcset") ?? "", map),
    );
  }
  const mapCss = (css: string) =>
    css.replace(
      URL_FUNCTION_RE,
      (match, doubleQuoted, singleQuoted, unquoted) => {
        const url = doubleQuoted ?? singleQuoted ?? unquoted;
        return map(url) === url ? match : `url("${map(url)}")`;
      },
    );
  for (const el of getElementsWithStyle(root)) {
    el.setAttribute("style", mapCss(el.getAttribute("style") ?? ""));
  }
  for (const el of Array.from(root.querySelectorAll("style"))) {
    el.textContent = mapCss(el.textContent ?? "");
  }
}

/**
 * Rewrites image references inside an ad's HTML root, doing three things:
 *
 *  1. `ad:filename` refs are resolved against `componentAssets` and replaced
 *     with the regional `${apiBase}/agents/files?key=...` URL, or with the
 *     signed URL from `imageHints.directUrls` when it has the filename.
 *  2. Any `agents/files?key=...` URL (whether produced by step 1 or already
 *     baked into the saved HTML by a previous editor round-trip) gets
 *     `width`/`height`/`format` params from `imageHints` injected.
 *  3. On image and use elements that map to a known component asset, the original
 *     `ad:filename` ref is recorded in an attribute-specific data attribute
 *     so template save can restore SVG references and image layers can restore src. This
 *     also auto-migrates legacy HTML that has the resolved URL baked in,
 *     as long as the URL's `key` matches a current component asset.
 *
 * Each pass is independent: passing only hints (no componentAssets) still
 * applies them to baked URLs; passing only assets/apiBase still resolves
 * `ad:` refs. Callers that want pixel-perfect output (e.g. capture-html-preview)
 * just omit `imageHints`.
 */
export function resolveAdRefs(
  root: Element,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string | undefined,
  imageHints?: AdRefImageHints,
): void {
  const canResolveAdRefs = Boolean(componentAssets && apiBase);
  const canInjectHints = hasHints(imageHints);
  if (!canResolveAdRefs && !canInjectHints) return;

  const reverseAssetMap = componentAssets
    ? buildReverseAssetMap(componentAssets)
    : null;

  for (const { selector, attr } of AD_REF_ATTRIBUTES) {
    for (const el of getMatchingElements(root, selector)) {
      processAttribute(
        el,
        attr,
        componentAssets,
        apiBase,
        imageHints,
        reverseAssetMap,
      );
    }
  }

  for (const el of getMatchingElements(root, SRCSET_SELECTOR)) {
    const srcset = el.getAttribute("srcset") ?? "";
    const next = mapSrcsetUrls(srcset, (url) =>
      rewriteUrl(url, componentAssets, apiBase, imageHints),
    );
    if (next !== srcset) el.setAttribute("srcset", next);
  }

  for (const el of getElementsWithStyle(root)) {
    const style = el.getAttribute("style");
    if (!style) continue;
    const next = rewriteUrlFunctions(
      style,
      componentAssets,
      apiBase,
      imageHints,
    );
    if (next !== style) {
      el.setAttribute("style", next);
    }
  }

  for (const styleEl of Array.from(root.querySelectorAll("style"))) {
    const css = styleEl.textContent;
    if (!css) continue;
    const next = rewriteUrlFunctions(css, componentAssets, apiBase, imageHints);
    if (next !== css) {
      styleEl.textContent = next;
    }
  }
}

/**
 * Convenience for raw markup: parse, resolve refs in place, serialize. Used
 * by callers that don't otherwise have a DOM tree (e.g. the Figma-plugin
 * endpoint returning legacy SVG ads). The output is the body's innerHTML so
 * trailing wrappers from the DOMParser aren't included.
 */
export function resolveAdRefsInMarkup(
  markup: string,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string | undefined,
  imageHints?: AdRefImageHints,
): string {
  if (!markup) return markup;
  const canResolveAdRefs = Boolean(componentAssets && apiBase);
  const canInjectHints = hasHints(imageHints);
  if (!canResolveAdRefs && !canInjectHints) return markup;

  const dom = new DOMParser().parseFromString(
    `<!DOCTYPE html><body>${markup}</body>`,
    "text/html",
  );
  const body = dom.body;
  if (!body) return markup;
  resolveAdRefs(body, componentAssets, apiBase, imageHints);
  return body.innerHTML;
}

function adRefDataAttribute(attr: string): string {
  return attr === "src"
    ? AD_REF_DATA_ATTR
    : `${AD_REF_DATA_ATTR}-${attr.replace(":", "-")}`;
}

/** Restore portable references at the template save boundary, including inside opaque SVG layers. */
export function restoreAdRefsInMarkup(markup: string): string {
  if (!markup.includes(AD_REF_DATA_ATTR)) return markup;
  const doc = new DOMParser().parseFromString(markup, "text/html");
  for (const { selector, attr } of AD_REF_ATTRIBUTES) {
    const dataAttr = adRefDataAttribute(attr);
    for (const el of getMatchingElements(doc.body, selector)) {
      const preserved = el.getAttribute(dataAttr);
      if (preserved?.startsWith(AD_REF_PREFIX)) {
        el.setAttribute(attr, preserved);
      }
      el.removeAttribute(dataAttr);
    }
  }
  return doc.body.innerHTML;
}

function processAttribute(
  el: Element,
  attr: string,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string | undefined,
  imageHints: AdRefImageHints | undefined,
  reverseAssetMap: Map<string, string> | null,
): void {
  const current = el.getAttribute(attr);
  if (!current) return;

  // Keep href and xlink:href separate because one SVG element can carry both.
  const dataAttr = adRefDataAttribute(attr);
  const preserved = preservedAdRef(current, reverseAssetMap);
  if (preserved && el.getAttribute(dataAttr) !== preserved) {
    el.setAttribute(dataAttr, preserved);
  }

  const next = rewriteUrl(current, componentAssets, apiBase, imageHints);
  if (next !== current) {
    el.setAttribute(attr, next);
  }
}

function preservedAdRef(
  url: string,
  reverseAssetMap: Map<string, string> | null,
): string | null {
  if (url.startsWith(AD_REF_PREFIX)) return url;
  if (!reverseAssetMap) return null;
  const filename = extractAdFilenameFromAgentFileUrl(url, reverseAssetMap);
  return filename ? `${AD_REF_PREFIX}${filename}` : null;
}

function hasHints(
  hints: AdRefImageHints | undefined,
): hints is AdRefImageHints {
  return Boolean(
    hints &&
      (hints.width ||
        hints.height ||
        hints.format ||
        hints.targetRenderer === "figma"),
  );
}

function buildReverseAssetMap(
  componentAssets: Record<string, string>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const [filename, s3Key] of Object.entries(componentAssets)) {
    // If two filenames map to the same key, last one wins; harmless for our
    // round-trip use since either name produces the same resolved URL.
    map.set(s3Key, filename);
  }
  return map;
}

export function getElementsWithStyle(root: Element): Element[] {
  const elements = Array.from(root.querySelectorAll("[style]"));
  if (root.hasAttribute("style")) {
    return [root, ...elements];
  }
  return elements;
}

export function getMatchingElements(
  root: Element,
  selector: string,
): Element[] {
  const elements = Array.from(root.querySelectorAll(selector));
  if (root.matches(selector)) {
    return [root, ...elements];
  }
  return elements;
}

function rewriteUrlFunctions(
  value: string,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string | undefined,
  imageHints: AdRefImageHints | undefined,
): string {
  return value.replace(
    URL_FUNCTION_RE,
    (match, doubleQuoted, singleQuoted, unquoted) => {
      const url = doubleQuoted ?? singleQuoted ?? unquoted;
      const rewritten = rewriteUrl(url, componentAssets, apiBase, imageHints);
      return rewritten === url ? match : `url("${rewritten}")`;
    },
  );
}

function rewriteUrl(
  url: string,
  componentAssets: Record<string, string> | null | undefined,
  apiBase: string | undefined,
  imageHints: AdRefImageHints | undefined,
): string {
  if (url.startsWith(AD_REF_PREFIX)) {
    if (!componentAssets || !apiBase) return url;
    return resolveAdRef(url, componentAssets, apiBase, imageHints) ?? url;
  }
  if (hasHints(imageHints)) {
    return injectHintsIntoAgentFileUrl(url, imageHints);
  }
  return url;
}

function resolveAdRef(
  adRef: string,
  componentAssets: Record<string, string>,
  apiBase: string,
  imageHints: AdRefImageHints | undefined,
): string | null {
  const filename = adRef.slice(AD_REF_PREFIX.length);
  const s3Key = componentAssets[filename];
  if (!s3Key) return null;
  const directUrls = imageHints?.directUrls;
  // Filenames are agent- and user-supplied, so `directUrls[filename]` alone
  // would resolve to an Object.prototype member for a name like `constructor`.
  if (directUrls && Object.hasOwn(directUrls, filename)) {
    const directUrl = directUrls[filename];
    if (directUrl !== undefined) return directUrl;
  }
  const params = new URLSearchParams({ key: s3Key });
  if (imageHints?.width) params.set("width", String(imageHints.width));
  if (imageHints?.height) params.set("height", String(imageHints.height));
  if (imageHints?.format) params.set("format", imageHints.format);
  if (imageHints?.targetRenderer === "figma") {
    params.set("targetRenderer", "figma");
  }
  return `${apiBase}/agents/files?${params.toString()}`;
}

function extractAdFilenameFromAgentFileUrl(
  url: string,
  reverseAssetMap: Map<string, string>,
): string | null {
  const parsed = parseAgentFileUrl(url);
  if (!parsed) return null;
  const key = parsed.searchParams.get("key");
  return key ? (reverseAssetMap.get(key) ?? null) : null;
}
